import { blue, red } from "@mui/material/colors";

import { HZ_VALUES } from "constants/audiogram";
import {
  AUDIOGRAM_MASKED_INDICATOR,
  AUDIOGRAM_PURE_TONE_ABSENT,
  SEGMENTING_VALUES
} from "constants/audiology";
import { isBlank } from "utils/common";

import {
  getAirConductionPointStyle,
  getBoneConductionPointStyle,
  getMCLPointStyle,
  getSoundfieldAidedPointStyle,
  getSoundfieldUnaidedPointStyle,
  getUCLPointStyle
} from "../../utils";
import { PureToneGraphItem } from "../pure-tone-grap-item.type";

const skipped = (ctx: any, value: any, annotations: boolean[]) => {
  return annotations[ctx.p0DataIndex] || annotations[ctx.p1DataIndex]
    ? value
    : undefined;
};

export const getGraphDataRow = (
  annotationRow: (string | null)[],
  numericRow: (string | null)[]
) => {
  if (!annotationRow || !numericRow) return [];

  return annotationRow?.map((annotationValue, index) => {
    const numericValue = numericRow[index];

    if (
      !isBlank(annotationValue as any) &&
      Number.isInteger(Number.parseInt(String(annotationValue)))
    )
      return Number(annotationValue);

    if (
      !isBlank(numericValue) &&
      Number.isInteger(Number.parseInt(String(numericValue)))
    )
      return Number(numericValue);

    return null;
  });
};

export const getMaskingIndicatorRow = (
  annotationRow: (string | null)[],
  numericRow: (string | null)[]
) => {
  const row = getGraphDataRow(annotationRow, numericRow);
  return row.map((v, index) =>
    v === null &&
    String(annotationRow[index] ?? "")
      .trim()
      .toUpperCase() === AUDIOGRAM_MASKED_INDICATOR
      ? AUDIOGRAM_MASKED_INDICATOR
      : v
  );
};

export const adjustDataRowsForVisualization = (
  acValues: Array<number | null>,
  bcValues: Array<number | null>
) => {
  if (acValues?.length !== bcValues?.length) {
    return [acValues, bcValues];
  }

  for (let i = 0; i < acValues.length; i++) {
    if (acValues[i] === null || bcValues[i] === null) continue;

    const aValue = Number(acValues[i]);
    const bValue = Number(bcValues[i]);

    let delta = Math.ceil(Math.abs(aValue - bValue));

    if (delta === 0) {
      delta = aValue >= bValue ? 1.5 : -1.5;
      bcValues[i] = bValue - delta;
    }
  }

  return [acValues, bcValues];
};

// Mirrors adjustDataRowsForVisualization, but across ears for UCL:
// when left and right UCL share a value at the same frequency, shift
// left UCL by -1.5 dB so both "U" sprites remain visible. Right UCL is
// returned unchanged so the right-ear marker stays at the recorded value.
export const adjustUclRowsForVisualization = (
  leftUcl: Array<number | null>,
  rightUcl: Array<number | null>
) => {
  if (leftUcl?.length !== rightUcl?.length) {
    return [leftUcl, rightUcl];
  }

  const adjustedLeft = [...leftUcl];
  for (let i = 0; i < adjustedLeft.length; i++) {
    if (adjustedLeft[i] === null || rightUcl[i] === null) continue;

    if (Number(adjustedLeft[i]) === Number(rightUcl[i])) {
      adjustedLeft[i] = Number(adjustedLeft[i]) - 1.5;
    }
  }

  return [adjustedLeft, rightUcl];
};

export const formatGraphDataset = (
  items: PureToneGraphItem[],
  pointStyles: any,
  useLogScale: boolean = false,
  visibleFrequencies: number[] = HZ_VALUES
) => {
  const result: any = [];
  if (!items?.length) return result;

  // When the x-axis is logarithmic, Chart.js requires explicit numeric
  // x values per point. We preserve nulls (as { x, y: null }) so that
  // spanGaps, pointRadius, and pointStyle arrays stay index-aligned with
  // the original HZ_VALUES-indexed rows.
  const visibleIndices = visibleFrequencies.map((hz) => HZ_VALUES.indexOf(hz));
  const isHiding = visibleIndices.length !== HZ_VALUES.length;
  const clip = (row: Array<number | null>) =>
    isHiding ? visibleIndices.map((i) => row[i] ?? null) : row;
  const toPoints = (row: Array<number | null>) =>
    clip(row).map((v, i) => ({ x: visibleFrequencies[i], y: v }));
  const formatData = (row: Array<number | null>) =>
    useLogScale ? toPoints(row) : clip(row);

  function getSegments(
    annotation: (string | null)[],
    numeric: (string | null)[]
  ) {
    let lastValue = false;
    const result = [];
    for (let i = 0; i < annotation.length; i++) {
      if (annotation[i] === null) {
        result.push(lastValue);
      } else if (Number.isInteger(Number.parseInt(annotation[i] as string))) {
        lastValue = false;
        result.push(lastValue);
      } else {
        if (
          SEGMENTING_VALUES.includes(annotation[i] as string) &&
          numeric[i] !== null
        ) {
          lastValue = true;
          result.push(lastValue);
        } else {
          result.push(lastValue);
        }
      }
    }
    return result;
  }

  // Precompute left/right UCL rows once so we can stagger left when it
  // collides with right at the same frequency. Keeping this outside the
  // per-item loop is what lets the helper see both ears at once.
  const leftItem = items.find((i) => i.ear === "left");
  const rightItem = items.find((i) => i.ear === "right");
  const [adjustedLeftUcl, adjustedRightUcl] = adjustUclRowsForVisualization(
    leftItem
      ? getGraphDataRow(leftItem.data.uclAnnotation, leftItem.data.ucl)
      : [],
    rightItem
      ? getGraphDataRow(rightItem.data.uclAnnotation, rightItem.data.ucl)
      : []
  );

  items.forEach(({ ear, data, hiddenData }) => {
    const [acData, bcData] = adjustDataRowsForVisualization(
      getGraphDataRow(data.airConductionAnnotation, data.airConduction),
      getGraphDataRow(data.boneConductionAnnotation, data.boneConduction)
    );
    let uclData: Array<number | null>;
    if (ear === "left") {
      uclData = adjustedLeftUcl;
    } else if (ear === "right") {
      uclData = adjustedRightUcl;
    } else {
      uclData = getGraphDataRow(data.uclAnnotation, data.ucl);
    }

    const pointRadii = acData?.map(
      (v: number | string | null) => (v === null ? 0 : 3) // Default is 3 according to Chart.js docs
    );

    result.push({
      spanGaps: true,
      type: "line" as const,
      label: `Air Conduction ${ear} ear`,
      data: formatData(acData),
      hidden: hiddenData.airConduction,
      borderWidth: 1,
      borderColor: ear === "left" ? blue[900] : red[900],
      backgroundColor: ear === "left" ? blue[900] : red[900],
      pointStyle: getMaskingIndicatorRow(
        data.airConductionMaskingLevelAnnotation,
        data.airConductionMaskingLevel
      ).map(
        (v, index) =>
          pointStyles[
            getAirConductionPointStyle(
              ear,
              v,
              data.airConductionAnnotation[index] === AUDIOGRAM_PURE_TONE_ABSENT
            )
          ]
      ),
      pointRadius: pointRadii,
      pointHitRadius: pointRadii,
      segment: {
        borderColor: (ctx: any) =>
          skipped(
            ctx,
            "rgb(0,0,0,0.2)",
            getSegments(data.airConductionAnnotation, data.airConduction)
          ),
        borderDash: (ctx: any) =>
          skipped(
            ctx,
            [6, 6],
            getSegments(data.airConductionAnnotation, data.airConduction)
          )
      }
    });

    result.push({
      type: "scatter" as const,
      label: `Bone Conduction ${ear} ear`,
      data: formatData(bcData),
      hidden: hiddenData.boneConduction,
      borderWidth: 1,
      borderColor: ear === "left" ? blue[900] : red[900],
      backgroundColor: ear === "left" ? blue[900] : red[900],
      pointStyle: getMaskingIndicatorRow(
        data.boneConductionMaskingLevelAnnotation,
        data.boneConductionMaskingLevel
      ).map(
        (v, index) =>
          pointStyles[
            getBoneConductionPointStyle(
              ear,
              v,
              data.boneConductionAnnotation[index] ===
                AUDIOGRAM_PURE_TONE_ABSENT
            )
          ]
      )
    });
    result.push({
      type: "scatter" as const,
      label: `MCL ${ear} ear`,
      data: formatData(getGraphDataRow(data.mclAnnotation, data.mcl)),
      hidden: hiddenData.mcl,
      borderWidth: 1,
      pointStyle: pointStyles[getMCLPointStyle(ear)]
    });
    result.push({
      type: "scatter" as const,
      label: `UCL ${ear} ear`,
      data: formatData(uclData),
      hidden: hiddenData.ucl,
      borderWidth: 1,
      pointStyle: data.uclAnnotation.map(
        (_: string | null, index: number) =>
          pointStyles[
            getUCLPointStyle(
              ear,
              data.uclAnnotation[index] === AUDIOGRAM_PURE_TONE_ABSENT
            )
          ]
      )
    });

    const soundFieldAidedDataRow = getGraphDataRow(
      data.soundfieldAidedAnnotation,
      data.soundfieldAided
    );
    result.push({
      spanGaps: true,
      type: "line" as const,
      label: `SoundField Aided ${ear} ear`,
      data: formatData(soundFieldAidedDataRow),
      hidden: hiddenData.soundfieldAided,
      borderWidth: 1,
      borderColor: ear === "left" ? blue[900] : red[900],
      backgroundColor: ear === "left" ? blue[900] : red[900],
      pointStyle: soundFieldAidedDataRow.map(
        (v, index) =>
          pointStyles[
            getSoundfieldAidedPointStyle(
              ear,
              data.soundfieldAidedAnnotation[index] ===
                AUDIOGRAM_PURE_TONE_ABSENT
            )
          ]
      ),
      segment: {
        borderColor: (ctx: any) =>
          skipped(
            ctx,
            "rgb(0,0,0,0.2)",
            getSegments(data.soundfieldAidedAnnotation, data.soundfieldAided)
          ),
        borderDash: (ctx: any) =>
          skipped(
            ctx,
            [6, 6],
            getSegments(data.soundfieldAidedAnnotation, data.soundfieldAided)
          )
      }
    });

    const soundFieldUnaidedDataRow = getGraphDataRow(
      data.soundfieldUnaidedAnnotation,
      data.soundfieldUnaided
    );
    result.push({
      spanGaps: true,
      type: "line" as const,
      label: `SoundField Unaided ${ear} ear`,
      data: formatData(soundFieldUnaidedDataRow),
      hidden: hiddenData.soundfieldUnaided,
      borderWidth: 1,
      borderColor: ear === "left" ? blue[900] : red[900],
      backgroundColor: ear === "left" ? blue[900] : red[900],
      pointStyle: soundFieldUnaidedDataRow.map(
        (v, index) =>
          pointStyles[
            getSoundfieldUnaidedPointStyle(
              ear,
              data.soundfieldUnaidedAnnotation[index] ===
                AUDIOGRAM_PURE_TONE_ABSENT
            )
          ]
      ),
      segment: {
        borderColor: (ctx: any) =>
          skipped(
            ctx,
            "rgb(0,0,0,0.2)",
            getSegments(
              data.soundfieldUnaidedAnnotation,
              data.soundfieldUnaided
            )
          ),
        borderDash: (ctx: any) =>
          skipped(
            ctx,
            [6, 6],
            getSegments(
              data.soundfieldUnaidedAnnotation,
              data.soundfieldUnaided
            )
          )
      }
    });
  });

  return result;
};
